import { listOfStateAbbreviations } from "@/lib/state-abbreviations";

import type { DoseSpotPharmacySearchParams } from "@/types/dosespot";

const STATE_ABBREVIATIONS = new Set(Object.keys(listOfStateAbbreviations));

const STATE_NAMES = new Map(
  Object.entries(listOfStateAbbreviations).map(([abbreviation, name]) => [
    name.toLowerCase(),
    abbreviation,
  ]),
);

const STREET_SUFFIXES = new Set([
  "ave",
  "avenue",
  "av",
  "st",
  "street",
  "rd",
  "road",
  "blvd",
  "boulevard",
  "dr",
  "drive",
  "ln",
  "lane",
  "way",
  "pkwy",
  "parkway",
  "hwy",
  "highway",
  "ct",
  "court",
  "pl",
  "place",
  "cir",
  "circle",
  "ste",
  "suite",
  "unit",
  "apt",
  "fl",
  "floor",
  "trl",
  "trail",
  "ter",
  "terrace",
  "loop",
  "pike",
  "expy",
  "fwy",
  "sq",
]);

const ZIP_PATTERN = /\b(\d{5})(?:-\d{4})?\b/;
const HOUSE_NUMBER_PATTERN = /^\d+[a-zA-Z]?$/;

/** DoseSpot rejects a name shorter than this with a 400. */
const MIN_NAME_LENGTH = 3;

export interface PharmacySearchCandidate {
  params: DoseSpotPharmacySearchParams;
  /** Only issued when the earlier candidates come back empty. */
  deferred?: boolean;
}

interface ParsedTerm {
  name?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

function matchTrailingState(
  segment: string,
): { state: string; rest: string } | null {
  const tokens = segment.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return null;

  const last = tokens[tokens.length - 1];
  if (last.length === 2 && STATE_ABBREVIATIONS.has(last.toUpperCase())) {
    return { state: last.toUpperCase(), rest: tokens.slice(0, -1).join(" ") };
  }
  for (let size = 3; size >= 1; size--) {
    if (tokens.length < size) continue;
    const tail = tokens
      .slice(-size)
      .join(" ")
      .toLowerCase()
      .replace(/[.,]/g, "");
    const state = STATE_NAMES.get(tail);
    if (state) return { state, rest: tokens.slice(0, -size).join(" ") };
  }
  return null;
}

function splitSegment(segment: string) {
  const tokens = segment.replace(/\./g, " ").split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return { name: "", street: "", city: "" };

  const suffixAt = tokens.findIndex(
    (token, index) =>
      index >= 1 && STREET_SUFFIXES.has(token.toLowerCase().replace(/,/g, "")),
  );
  if (suffixAt !== -1) {
    let start = suffixAt - 1;
    if (start - 1 >= 0 && HOUSE_NUMBER_PATTERN.test(tokens[start - 1]))
      start -= 1;
    return {
      name: tokens.slice(0, start).join(" "),
      street: tokens.slice(start, suffixAt + 1).join(" "),
      city: tokens.slice(suffixAt + 1).join(" "),
    };
  }
  if (HOUSE_NUMBER_PATTERN.test(tokens[0]) && tokens.length > 1) {
    return { name: "", street: tokens.join(" "), city: "" };
  }
  return { name: tokens.join(" "), street: "", city: "" };
}

export function parsePharmacyTerm(term: string): ParsedTerm {
  const parsed: ParsedTerm = {};
  let rest = term.trim();

  const zipMatch = ZIP_PATTERN.exec(rest);
  if (zipMatch) {
    parsed.zipCode = zipMatch[1];
    rest =
      `${rest.slice(0, zipMatch.index)} ${rest.slice(zipMatch.index + zipMatch[0].length)}`.trim();
  }

  let segments = rest
    .split(",")
    .map((segment) => segment.trim())
    .filter(Boolean);

  if (segments.length > 0) {
    const match = matchTrailingState(segments[segments.length - 1]);
    if (match) {
      const remaining = [...segments.slice(0, -1), match.rest].filter(Boolean);
      // Only claim a state when something else survives — a bare "New York" is a name.
      if (remaining.length > 0) {
        parsed.state = match.state;
        segments = remaining;
      }
    }
  }

  const names: string[] = [];
  const cities: string[] = [];
  let sawStreet = false;

  segments.forEach((segment, index) => {
    const { name, street, city } = splitSegment(segment);
    if (
      segments.length > 1 &&
      index === segments.length - 1 &&
      name &&
      !street &&
      !city
    ) {
      cities.push(name);
      return;
    }
    if (name) names.push(name);
    if (street) sawStreet = true;
    if (city) cities.push(city);
  });

  if (cities.length > 0) parsed.city = cities[cities.length - 1];
  if (names.length > 0) parsed.name = names.join(" ");
  else if (!sawStreet && cities.length === 0 && segments.length > 0)
    parsed.name = segments[0];

  return parsed;
}

/**
 * A street address is deliberately dropped: DoseSpot ANDs every field, and its
 * directory rarely matches the street a patient writes down.
 */
function toParams(parsed: ParsedTerm): DoseSpotPharmacySearchParams | null {
  const params: DoseSpotPharmacySearchParams = {};
  if (parsed.name) params.name = parsed.name;
  if (parsed.city) params.city = parsed.city;
  if (parsed.state) params.state = parsed.state;
  if (parsed.zipCode) params.zipCode = parsed.zipCode;

  if (params.name && params.name.length < MIN_NAME_LENGTH) return null;
  // A city- or state-only query returns arbitrary nationwide rows, so every
  // candidate has to be anchored by a name or a ZIP.
  if (!params.name && !params.zipCode) return null;
  // Every field is ANDed, so a city on top of a ZIP only narrows a search the
  // ZIP already localises — down to so few rows that DoseSpot pads the page out
  // with nationwide filler.
  if (!params.name && params.zipCode) delete params.city;
  return params;
}

function currentBehaviourParams(
  term: string,
): DoseSpotPharmacySearchParams | null {
  const trimmed = term.trim();
  if (/^\d{5}(-\d{4})?$/.test(trimmed)) return { zipCode: trimmed };
  return toParams({ name: trimmed });
}

/**
 * Turns one typed term into the DoseSpot queries worth running, best first.
 *
 * The term the user typed is always one of the candidates, so the merged result
 * set can never be smaller than searching on that term alone.
 */
export function pharmacySearchPlan(term: string): PharmacySearchCandidate[] {
  const candidates: PharmacySearchCandidate[] = [];
  const seen = new Set<string>();

  const add = (
    params: DoseSpotPharmacySearchParams | null,
    options: { deferred?: boolean } = {},
  ) => {
    if (!params) return;
    const key = JSON.stringify(params);
    if (seen.has(key)) return;
    seen.add(key);
    candidates.push({ params, deferred: options.deferred });
  };

  const parsed = parsePharmacyTerm(term);
  add(toParams(parsed));

  // One un-comma'd chunk is ambiguous between "<brand> <town>" and a real
  // multi-word store name, so try it whole above and split only if that misses.
  if (parsed.name?.includes(" ") && !parsed.city) {
    const tokens = parsed.name.split(/\s+/);
    add(
      toParams({
        ...parsed,
        name: tokens.slice(0, -1).join(" "),
        city: tokens[tokens.length - 1],
      }),
      { deferred: true },
    );
  }

  add(currentBehaviourParams(term));
  return candidates;
}
